import { motion } from 'framer-motion'
import { Phone, Mail, ArrowRight, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'
import { footer, phone } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

export default function Footer() {
  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-slate-950 text-slate-400"
    >
      {/* Upper footer */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-5">
              <img
                src={`${import.meta.env.BASE_URL}footer-logo.webp`}
                alt="Douglas P Nelson"
                className="h-12 w-auto brightness-0 invert opacity-70 hover:opacity-100 hover:scale-105 transition-all duration-300"
                onError={(e) => {
                  const el = e.target as HTMLImageElement
                  el.style.display = 'none'
                  const sibling = el.nextSibling as HTMLElement | null
                  if (sibling) sibling.style.display = 'block'
                }}
              />
              <span className="hidden text-white font-bold text-lg">DP Nelson</span>
            </Link>
            <p className="text-sm leading-relaxed text-slate-500 mb-6 max-w-xs">
              Internal Family Systems coaching for professionals navigating life's transitions. Working virtually with clients worldwide.
            </p>
            <CalendlyBtn size="sm" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
              Book Free Consultation
            </CalendlyBtn>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-slate-300 text-xs font-semibold mb-4 uppercase tracking-widest">Explore</p>
            <ul className="space-y-2.5">
              {[
                { to: '/about', label: 'About Douglas' },
                { to: '/ifs', label: 'What is IFS?' },
                { to: '/coaching-vs-therapy', label: 'Coaching vs. Therapy' },
                { to: '/services', label: 'Services' },
                { to: '/blog', label: 'Blog' },
                { to: '/resources', label: 'Client Resources' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="text-sm text-slate-500 hover:text-slate-300 transition-colors flex items-center gap-1.5 group">
                    <ArrowRight size={12} className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-slate-300 text-xs font-semibold mb-4 uppercase tracking-widest">Contact</p>
            <div className="space-y-3">
              <a href={`tel:${phone}`}
                className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-slate-300 transition-colors">
                <Phone size={13} className="flex-shrink-0 text-slate-600" />
                {phone}
              </a>
              <a href="mailto:dpnelsons@gmail.com"
                className="flex items-center gap-2.5 text-sm text-slate-500 hover:text-slate-300 transition-colors">
                <Mail size={13} className="flex-shrink-0 text-slate-600" />
                dpnelsons@gmail.com
              </a>
              <p className="flex items-center gap-2.5 text-sm text-slate-600">
                <MapPin size={13} className="flex-shrink-0" />
                California · Virtual, worldwide
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lower footer */}
      <div className="border-t border-slate-900 py-5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-slate-700">{footer.copyright}</p>
          <div className="flex items-center gap-4">
            <Link
              to="/404"
              className="text-xs text-slate-800 hover:text-slate-600 transition-colors italic"
              title="Lost? We've got you."
            >
              Lost? →
            </Link>
            <p className="text-xs text-slate-700">Built with Vite + React</p>
          </div>
        </div>
      </div>
    </motion.footer>
  )
}
